import { useState } from 'react'

import Styles from './ProvideReason.module.scss'

import { Text } from 'blaze-ui/Text/Text'
import { Button } from 'blaze-ui/Button'
import { Checkbox } from 'components/Form/Checkbox/Checkbox'
import { WorkspaceClient } from 'api/clients/WorkspaceClient'
import { useMutation } from 'react-query'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { CancellationReason } from '../PaymentForm/PaymentForm'
import dayjs, { Dayjs } from 'dayjs'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { Modal } from 'almanac-ui/Modal'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Input } from 'almanac-ui/TextInputField/Input'
import { TextAreaField } from 'components/Form/TextAreaField'

interface Props {
  onCancel: () => void
  onNext: (periodEnd: Dayjs) => void
}

export enum Reason {
  LackingIntegrations = 'lacking_integrations',
  BadAiGenerations = 'bad_ai_generations',
  LackingEditingFeatures = 'lacking_editing_features',
  DoesNotFitNeeds = 'does_not_fit_needs',
  BadReliabilityOrPerformance = 'bad_reliability_or_performance',
  NotWorthValue = 'not_worth_value',
  TooComplex = 'too_complex',
  CustomerService = 'customer_service',
  SwitchedService = 'switched_service',
  Other = 'other',
}

const reasonLabels: Record<Reason, string> = {
  [Reason.LackingIntegrations]: "Doesn't have the integrations I need",
  [Reason.BadAiGenerations]: 'AI generations not good enough',
  [Reason.LackingEditingFeatures]: 'Not enough editing features',
  [Reason.DoesNotFitNeeds]: 'No longer fits my needs',
  [Reason.BadReliabilityOrPerformance]: 'Reliability or performance concerns',
  [Reason.NotWorthValue]: "The pricing isn't worth the value",
  [Reason.TooComplex]: "I couldn't figure out how to use it",
  [Reason.CustomerService]: 'Customer service was unsatisfactory',
  [Reason.SwitchedService]: 'I found another tool I like better',
  [Reason.Other]: 'Other',
}

const reasonPlaceholders: Record<Reason, string | null> = {
  [Reason.LackingIntegrations]: 'Which integrations would you like us to add?',
  [Reason.BadAiGenerations]: "Which content types weren't good enough for you?",
  [Reason.LackingEditingFeatures]: 'Which editing features were we missing?',
  [Reason.DoesNotFitNeeds]: 'What would make you stay around?',
  [Reason.BadReliabilityOrPerformance]: 'What specifically concerned you?',
  [Reason.NotWorthValue]: 'What would make the price worth the value for you?',
  [Reason.TooComplex]: 'What specifically was difficult to use?',
  [Reason.CustomerService]: null,
  [Reason.SwitchedService]: 'Which tool are you switching to?',
  [Reason.Other]: 'Please add additional reasons',
}

export const ProvideReason = ({ onCancel, onNext }: Props) => {
  const cancelSubscriptionMutation = useMutation(WorkspaceClient.cancelSubscription)
  const workspaceId = useCurrentWorkspaceId()
  const [selectedReasons, setSelectedReasons] = useState<{ reason: Reason; description: string }[]>([])
  const [additionalFeedback, setAdditionalFeedback] = useState('')
  const [error, setError] = useState(false)

  const stripeCancellationReasonForReason = (reason: Reason) => {
    switch (reason) {
      case Reason.LackingIntegrations:
      case Reason.LackingEditingFeatures:
        return CancellationReason.MissingFeatures
      case Reason.BadAiGenerations:
      case Reason.BadReliabilityOrPerformance:
        return CancellationReason.LowQuality
      case Reason.DoesNotFitNeeds:
        return CancellationReason.Unused
      case Reason.NotWorthValue:
        return CancellationReason.TooExpensive
      case Reason.TooComplex:
        return CancellationReason.TooComplex
      case Reason.CustomerService:
        return CancellationReason.CustomerService
      case Reason.SwitchedService:
        return CancellationReason.SwitchedService
      case Reason.Other:
        return CancellationReason.Other
      default:
        return CancellationReason.Other
    }
  }

  const cancelSubscription = () => {
    if (selectedReasons.length === 0) {
      setError(true)
      return
    }

    // We are "massaging" the reasons we offer into the reasons that Stripe will actually accept.
    // Stripe only allows one reason, so we are just using the first.
    // We'll include all of the data in the description.
    const cancellationReason = stripeCancellationReasonForReason(selectedReasons[0].reason)

    const reasonsDescription = selectedReasons.map((sR) => `${sR.reason}: ${sR.description}`).join(' | ')
    const cancellationDescription = additionalFeedback
      ? `${reasonsDescription} | Additional Feedback: ${additionalFeedback}`
      : reasonsDescription

    cancelSubscriptionMutation.mutate(
      {
        workspaceId,
        cancellationReason,
        cancellationDescription,
      },
      {
        onSuccess: (res) => {
          const periodEnd = dayjs.unix(res.data.subscription.cancel_at)
          onNext(periodEnd)
        },
        onError: () => {
          showToastMessage('Unable to cancel your subscription', 'error')
        },
      },
    )
  }

  const handleCheckboxChange = (reason: Reason) => {
    setError(false)

    if (selectedReasons.map((r) => r.reason).includes(reason)) {
      setSelectedReasons(selectedReasons.filter((r) => r.reason !== reason))
      return
    }
    setSelectedReasons([...selectedReasons, { reason, description: '' }])
  }

  const handleInputChange = (value: string, reason: Reason) => {
    setSelectedReasons(
      selectedReasons.map((selectedReason) =>
        selectedReason.reason === reason ? { ...selectedReason, description: value } : selectedReason,
      ),
    )
  }

  return (
    <div className={Styles.container}>
      <Modal.Header title="Help us improve" onClose={onCancel} />

      <div className={Styles.content}>
        <div className={Styles.questionSection}>
          <Heading level={2}>Why are you cancelling?</Heading>
          {error ? (
            <Text variant="secondary" color="var(--red-70)">
              Must select at least one option *
            </Text>
          ) : (
            <Text variant="secondary" color="var(--dark-60)">
              Help us improve by letting us know why you're leaving. Select all that apply.
            </Text>
          )}
        </div>

        <div className={Styles.reasons}>
          {Object.values(Reason).map((reason) => (
            <div className={Styles.reasonContainer} key={reason}>
              <Checkbox
                size="md"
                isSelected={selectedReasons.map((r) => r.reason).includes(reason)}
                onChange={() => handleCheckboxChange(reason)}
                aria-label={reasonLabels[reason]}
              >
                <Text variant="primary" color="var(--dark-80)">
                  {reasonLabels[reason]}
                </Text>
              </Checkbox>
              {selectedReasons.map((r) => r.reason).includes(reason) && reasonPlaceholders[reason] && (
                <div className={Styles.input}>
                  <Input
                    name={`reason-${reason}`}
                    onChange={(event) => handleInputChange(event.target.value, reason)}
                    placeholder={reasonPlaceholders[reason] || undefined}
                    value={selectedReasons.find((r) => r.reason === reason)?.description || ''}
                  />
                </div>
              )}
            </div>
          ))}
        </div>

        <div className={Styles.feedbackSection}>
          <Heading level={4}>Additional feedback (optional)</Heading>
          <TextAreaField
            inputClassName={Styles.feedbackTextarea}
            placeholder="Tell us more about your experience..."
            value={additionalFeedback}
            onChange={(e) => setAdditionalFeedback(e.target.value)}
            rows={4}
          />
        </div>
      </div>

      <div className={Styles.footer}>
        <Button size="lg" variant="tertiary" onPress={onCancel}>
          Cancel
        </Button>
        <Button
          size="lg"
          variant="primary"
          onPress={cancelSubscription}
          isDisabled={cancelSubscriptionMutation.isLoading || error}
        >
          Next
        </Button>
      </div>
    </div>
  )
}
